import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import LeagueFilterV18 from "../LeagueFilterV18";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";
import FootballMatchHeader from "../FootballMatchHeader";
import FootballOddsMatchRow from "../FootballOddsMatchRow";
import BongDaNetListFilterDate from "../BongDaNetListFilterDate";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV18 = ({ type = "all" }) => {
  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorTextMuted,
    colorAccent,
    colorBorder,
    colorHalfScore,
    colorOnPrimary,
    colorBtnBorder,
    colorFilterActiveBg,
    colorLiveChipBg,
    colorTabActive,
    colorTabInactive,
    colorTabText,
    colorTableHeaderBg,
    colorTableHeaderText,
    colorHot,
    colorWarning,
    hasDataConfig,
  } = useCompConfig();
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState("live");
  const [isHotOnly, setIsHotOnly] = useState(false);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `Tỷ lệ ${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateOptions = useMemo(() => {
    const options = [];

    options.push({
      value: "live",
      label: "Kèo trực tuyến",
      type: "live",
    });

    options.push({
      value: today.format("YYYY-MM-DD"),
      label: "Hôm nay",
      type: "today",
    });

    const tomorrow = today.clone().add(1, "day");
    options.push({
      value: tomorrow.format("YYYY-MM-DD"),
      label: "Ngày mai",
      type: "tomorrow",
    });

    for (let i = 2; i <= 5; i++) {
      const d = today.clone().add(i, "day");
      options.push({
        value: d.format("YYYY-MM-DD"),
        label: d.format("DD/MM"),
        type: "future",
      });
    }

    return options;
  }, [todayString]);

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    hotLeaguesWithMatches,
    leaguesWithMatches,
    loading,
    hasMore,
    loadMoreRef,
    isLoadingMoreRef,
  } = useBangKeoMatches(queryObject);

  const baseLeaguesWithMatches = useMemo(
    () => (isHotOnly ? hotLeaguesWithMatches : leaguesWithMatches),
    [isHotOnly, hotLeaguesWithMatches, leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds);
    return (baseLeaguesWithMatches || []).filter(({ league }) =>
      idSet.has(league.id),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    setSelectedDate(todayString);
  }, [todayString]);

  const handleDateChange = (value) => {
    setIsHotOnly(false);
    setSelectedDate(value);
    setFirstLoad(false);
  };

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setSelectedDate("");
    setFirstLoad(false);
  };

  const filterRowRef = useRef(null);

  const themeStyle = hasDataConfig
    ? {
        ...(colorPrimary != null && { "--theme-primary": colorPrimary }),
        ...(colorSecondary != null && { "--theme-highlight": colorSecondary }),
        ...(colorBg != null && { "--theme-bg": colorBg }),
        ...(colorText != null && { "--theme-text": colorText }),
        ...(colorForeground != null && { "--theme-foreground": colorForeground }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorHalfScore != null && { "--text-half-score": colorHalfScore }),
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBtnBorder != null && { "--theme-btn-border": colorBtnBorder }),
        ...(colorTabActive != null && { "--theme-tab-active": colorTabActive }),
        ...(colorTabInactive != null && {
          "--theme-tab-inactive": colorTabInactive,
        }),
        ...(colorTabText != null && { "--theme-tab-text": colorTabText }),
        ...(colorTableHeaderBg != null && {
          "--theme-table-header-bg": colorTableHeaderBg,
        }),
        ...(colorTableHeaderText != null && {
          "--theme-table-header-text": colorTableHeaderText,
        }),
        ...((colorFilterActiveBg ?? colorHot) != null && {
          "--theme-filter-active-bg": colorFilterActiveBg ?? colorHot,
        }),
        ...((colorLiveChipBg ?? colorWarning) != null && {
          "--theme-live-chip-bg": colorLiveChipBg ?? colorWarning,
        }),
      }
    : undefined;

  return (
    <div
      className="mx-auto md:px-2 py-3 rounded-xl flex flex-col gap-3"
      style={themeStyle}
    >
      <div className="flex flex-col  gap-1 md:gap-2 md:bg-[var(--theme-foreground)] md:border md:border-solid md:border-[var(--theme-border)] md:rounded-[16px] md:p-[10px]">
        <div
          ref={filterRowRef}
          className="flex w-full min-w-0 flex-wrap items-center gap-2 rounded-xl bg-[var(--theme-bg)] md:rounded-none md:bg-transparent"
        >
          <LeagueFilterV18
            anchorRef={filterRowRef}
            selectedIds={selectedLeagueIds}
            onChange={handleLeagueFilterChange}
          />
        </div>
        <div className="flex w-full min-w-0 flex-col gap-2 rounded-xl bg-[var(--theme-bg)] md:flex-row md:flex-nowrap md:items-center md:gap-2 md:rounded-none md:bg-transparent">
          <div className="min-w-0 w-full md:flex-1 md:basis-0">
            <BongDaNetListFilterDate
              dateOptions={dateOptions}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              isHotOnly={isHotOnly}
              onToggleHot={() => setIsHotOnly((prev) => !prev)}
            />
          </div>
        </div>

        {loading && <LoadingInner />}
        {!loading && (
          <>
            <div className="flex w-full min-w-0 flex-col gap-1">
              {filteredLeaguesWithMatches.length > 0 && (
                <div className="">
                  {/* Mobile header (bongdanet-like) */}
                  <div
                    className="item lv2-head-title md:hidden grid grid-cols-[1fr_50px_1fr] items-center gap-0 bg-[var(--theme-primary)] p-[5px] text-[var(--theme-on-primary)]"
                    id="tr_0"
                  >
                    <div className="team text-start text-[12px] leading-[18px]">Trận đấu</div>
                    <div className="score text-center text-[12px] leading-[18px]" style={{ width: 50 }}>
                      Tỷ số
                    </div>
                    <div className="odds text-center text-[12px] leading-[18px]">Tỷ lệ</div>
                  </div>
                  <table className="w-full border-collapse" id="table_live">
                    <tbody>
                      <FootballMatchHeader
                        extendValue={bookmarkerExtend}
                        bookmakerValue={bookmarker}
                        extendOptions={bookmakerExtendOptions}
                        bookmakerOptions={bookmakerOptions}
                        onExtendChange={(val) =>
                          dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
                        }
                        onBookmakerChange={(val) =>
                          dispatch({ type: CHANGE_BOOKMAKER, payload: val })
                        }
                      />
                      {filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
                        matches.map((match, index) => (
                          <FootballOddsMatchRow
                            key={`${league?.id ?? "league"}-${match.id}-${bookmarker}`}
                            league={league}
                            match={match}
                            isFirstInLeague={index === 0}
                            isLastInLeague={index === matches.length - 1}
                          />
                        )),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {filteredLeaguesWithMatches.length === 0 && (
                <div className="space-y-2 rounded-lg">
                  <NoData message="Không có trận đấu nào" />
                </div>
              )}
            </div>
            {hasMore && !isHotOnly && (
              <div ref={loadMoreRef} className="py-4 text-center">
                {isLoadingMoreRef && <LoadingInner />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KbvTiLeV18;
